import axios from "axios";
import { jwtDecode } from "jwt-decode";

const API_BASE_URL = "https://heritagespringscommunity.myhoa.club/api".replace(/\/+$/, "");

const api = axios.create({
  baseURL: API_BASE_URL,
});

let isRefreshing = false;
let refreshSubscribers = [];

const AUTH_EXCLUDED_PATHS = [
  "/authenticate",
  "/refresh-token",
  "/request-access",
  "/reset-password",
  // Public voting. no user account needed, ballot token used for identity
  "/share/opened",        // track ballot open
  "/public/ballot",       // load election + questions for vote page
  "/public/vote",         // submit vote
];

const isAuthExcludedRoute = (url = "") => {
  return AUTH_EXCLUDED_PATHS.some((path) => url.includes(path));
};

const clearAuthStorage = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("username");
  localStorage.removeItem("firstName");
  localStorage.removeItem("lastName");
  localStorage.removeItem("roles");
  localStorage.removeItem("userId");
  localStorage.removeItem("roleId");
  sessionStorage.clear();
};

const redirectToLogin = () => {
  const path = window.location.pathname;
  // Never redirect public voters on the ballot/vote page to the login screen
  if (path !== "/login" && !path.startsWith("/vote/")) {
    const target = `${window.location.pathname}${window.location.search}`;
    window.location.href = `/login?redirect=${encodeURIComponent(target)}`;
  }
};

const signOutUser = () => {
  clearAuthStorage();
  redirectToLogin();
};

// Resolve all queued subscribers with the new token
const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken, null));
  refreshSubscribers = [];
};

// Reject all queued subscribers so their promises don't hang forever
const failRefreshSubscribers = (error) => {
  refreshSubscribers.forEach((cb) => cb(null, error));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem("refreshToken");

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await axios.post(`${API_BASE_URL}/refresh-token`, {
    refreshToken,
  });

  const newToken = response.data.token;
  localStorage.setItem("authToken", newToken);

  if (response.data.userId)  localStorage.setItem("userId",  response.data.userId);
  if (response.data.roleId)  localStorage.setItem("roleId",  response.data.roleId);
  if (response.data.roles)   localStorage.setItem("roles",   JSON.stringify(response.data.roles));

  return newToken;
};

// ── Request interceptor ───────────────────────────────────────────────────────
api.interceptors.request.use(
  async (config) => {
    const requestUrl = config?.url || "";

    if (isAuthExcludedRoute(requestUrl)) {
      if (!(config.data instanceof FormData)) {
        config.headers["Content-Type"] = "application/json";
      }
      return config;
    }

    let token = localStorage.getItem("authToken");

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);

        if (!decoded.exp || decoded.exp <= currentTime) {
          if (!isRefreshing) {
            isRefreshing = true;
            try {
              const newToken = await refreshAccessToken();
              isRefreshing = false;
              onRefreshed(newToken);
              token = newToken;
            } catch (refreshError) {
              isRefreshing = false;
              failRefreshSubscribers(refreshError); // unblock all waiting requests
              clearAuthStorage();
              redirectToLogin();
              return Promise.reject(refreshError);
            }
          } else {
            // Another refresh is already in flight. wait for it
            token = await new Promise((resolve, reject) => {
              addRefreshSubscriber((newToken, error) => {
                if (error) reject(error);
                else resolve(newToken);
              });
            });
          }
        }

        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        // jwtDecode threw (malformed token) or subscriber was rejected
        if (!isAuthExcludedRoute(requestUrl)) {
          clearAuthStorage();
          redirectToLogin();
        }
        return Promise.reject(error);
      }
    }

    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor ──────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const requestUrl = originalRequest?.url || "";

    if (isAuthExcludedRoute(requestUrl)) {
      return Promise.reject(error);
    }

    // 403 = permission denied. the token is valid, the user just doesn't have
    // access. Do NOT try to refresh; doing so can consume the refresh token and
    // trigger an unnecessary sign-out.
    if (status === 403) {
      return Promise.reject(error);
    }

    // 401 = token expired or invalid. attempt a silent refresh + retry
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const newToken = await refreshAccessToken();
            isRefreshing = false;
            onRefreshed(newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          } catch (refreshError) {
            isRefreshing = false;
            failRefreshSubscribers(refreshError); // unblock all waiting requests
            signOutUser();
            return Promise.reject(refreshError);
          }
        }

        // Refresh already in flight. wait for it to finish
        const newToken = await new Promise((resolve, reject) => {
          addRefreshSubscriber((token, error) => {
            if (error) reject(error);
            else resolve(token);
          });
        });

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (err) {
        // Subscriber was rejected (refresh failed elsewhere)
        signOutUser();
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

// Existing API Endpoints
export const fetchFolders = () => api.get("/folders");
export const fetchFiles = () => api.get("/files");
export const fetchViolations = () => api.get("/getViolations");
export const fetchGeneralRequests = () => api.get("/getGeneralRequests");
export const fetchArchitecturalRequests = () => api.get("/getArchitecturalRequests");
export const fetchAssignableUsers = () => api.get("/users-by-role");

export const uploadFile = (formData) => api.post("/upload", formData);

export const updateNotice = (id, notice) =>
  api.post(`/updateNotice/${id}`, { notice });

export const fetchNotices = () => api.get("/notices");

export const downloadFile = (filename) =>
  api.get(`/images/${filename}`, { responseType: "blob" });

// Calendar APIs
export const fetchEvents = () => api.get("/getEvents");
export const addEvent = (eventData) => api.post("/addEvent", eventData);
export const editEvent = (id, updatedData) =>
  api.put(`/editEvent/${id}`, updatedData);
export const deleteEvent = (id) => api.delete(`/deleteEvent/${id}`);

// User Management
export const fetchUsers = () => api.get("/users");

// Analytics
export const fetchTopMembers = () => api.get("/top-members");

export { API_BASE_URL, signOutUser };
export default api;
